import { useMemo, useState } from 'react';
import { positionAt } from '../lib/hermite';
import { jdToIso } from '../lib/ephemeris';
import { PLANETS, usePlanetsStore } from '../store/planetsStore';
import { useTimeline } from '../store/timelineStore';
import { useSelection } from '../store/selectionStore';
import { useSmallBodies } from '../store/smallBodiesStore';
import {
  FIELD_HELP,
  TYPE_LABELS,
  formatDay,
  formatTemperature,
  formatYear,
  getInfoById,
  getInfoBySmallBodyKey,
  type PlanetInfo,
  weightOnBody,
} from '../data/planets-info';

function fmtKm(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} ×10³ km`;
  return `${n} km`;
}

interface ComparisonBarProps {
  label: string;
  value: number;
  earthValue: number;
  unit?: string;
  format?: (n: number) => string;
  help?: string;
}

function ComparisonBar({ label, value, earthValue, unit, format, help }: ComparisonBarProps) {
  const ratio = value / earthValue;
  const widthPct = Math.min(100, Math.log10(1 + ratio) * 50);
  const fmt = format ?? ((n: number) => n.toLocaleString('fr-FR'));
  return (
    <div className="comparison-row">
      <div className="comparison-label">
        <span title={help}>{label}</span>
        <span className="comparison-ratio">
          {ratio < 0.01 ? '<0.01' : ratio < 10 ? `×${ratio.toFixed(2)}` : `×${Math.round(ratio).toLocaleString('fr-FR')}`}
        </span>
      </div>
      <div className="comparison-bar">
        <div className="comparison-fill" style={{ width: `${widthPct}%` }} />
        <div className="comparison-earth" style={{ left: `${Math.min(100, Math.log10(2) * 50)}%` }}
             title="Terre = référence" />
      </div>
      <div className="comparison-value">
        {fmt(value)}{unit ? ` ${unit}` : ''}
      </div>
    </div>
  );
}

function DetailFiche({ info, extraLines }: { info: PlanetInfo; extraLines: [string, string][] }) {
  const [showFunFacts, setShowFunFacts] = useState(true);
  const [showCompare, setShowCompare] = useState(false);
  const typeMeta = TYPE_LABELS[info.type];

  return (
    <>
      <div className="fiche-header">
        <div className="fiche-symbol" style={{ background: typeMeta.color }}>
          {info.symbol}
        </div>
        <div className="fiche-title">
          <h2>{info.name_fr}</h2>
          <p>{info.name_en} · <span className="fiche-badge" style={{ borderColor: typeMeta.color, color: typeMeta.color }}
              title={typeMeta.help}>{typeMeta.label}</span></p>
        </div>
      </div>

      {info.uncertainty && (
        <div className="fiche-warning">
          ⚠ <strong>{info.uncertainty}</strong>
        </div>
      )}

      <p className="fiche-summary">{info.summary}</p>

      {/* Stats clés */}
      <div className="fiche-stats">
        <Stat
          label="Diamètre"
          value={fmtKm(info.diameter_km)}
          sub={info.diameter_vs_earth >= 1
            ? `${info.diameter_vs_earth.toFixed(1)}× Terre`
            : `${(1 / info.diameter_vs_earth).toFixed(1)}× plus petit`}
          help={FIELD_HELP.diameter}
        />
        <Stat
          label="Distance ☉"
          value={info.distance_au === 0 ? '—' : `${info.distance_au.toFixed(2)} UA`}
          sub={info.distance_au === 0 ? 'centre' : `${(info.distance_au * 149.6).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} M km`}
          help={FIELD_HELP.distance}
        />
        <Stat
          label="Année"
          value={info.year_earth_days === 0 ? '—' : formatYear(info.year_earth_days)}
          sub={info.year_earth_days > 365 ? `${(info.year_earth_days / 365.25).toFixed(1)} ans terrestres` : 'année locale'}
          help={FIELD_HELP.year}
        />
        <Stat
          label="Jour"
          value={formatDay(info.day_length_hours)}
          help={FIELD_HELP.day}
        />
        <Stat
          label="Gravité"
          value={`${info.gravity_vs_earth.toFixed(2)} g`}
          sub={`${(info.gravity_vs_earth * 9.81).toFixed(1)} m/s²`}
          help={FIELD_HELP.gravity}
        />
        <Stat
          label="Temp."
          value={formatTemperature(info.temperature_c)}
          help={FIELD_HELP.temperature}
        />
        {info.moons_count > 0 && (
          <Stat
            label="Lunes"
            value={info.moons_count.toString()}
            help={FIELD_HELP.moons}
          />
        )}
      </div>

      {/* Highlight + "Si tu pesais 70 kg" */}
      <div className="fiche-highlight">{info.highlight}</div>

      {info.gravity_vs_earth > 0 && info.gravity_vs_earth !== 1 && (
        <div className="fiche-fact-card">
          <span className="fiche-fact-emoji">⚖️</span>
          <div>
            <strong>Si tu pesais 70 kg sur Terre…</strong>
            <br />
            <span style={{ color: 'var(--text-1)' }}>
              tu pèserais <strong>{weightOnBody(70, info)} kg</strong> sur {info.name_fr}.
            </span>
          </div>
        </div>
      )}

      {/* Toggles */}
      <div className="fiche-actions">
        <button
          className={showFunFacts ? 'active' : ''}
          onClick={() => setShowFunFacts((v) => !v)}
        >
          💡 Le savais-tu ?
        </button>
        <button
          className={showCompare ? 'active' : ''}
          onClick={() => setShowCompare((v) => !v)}
        >
          🌍 Comparer à la Terre
        </button>
      </div>

      {/* Fun facts */}
      {showFunFacts && info.fun_facts.length > 0 && (
        <div className="fiche-funfacts">
          {info.fun_facts.map((f, i) => (
            <div key={i} className="fiche-funfact">
              <span className="fiche-funfact-marker">{i + 1}</span>
              <span>{f}</span>
            </div>
          ))}
        </div>
      )}

      {/* Comparaisons visuelles */}
      {showCompare && info.distance_au > 0 && (
        <div className="fiche-comparison">
          <h3>Comparaison à la Terre</h3>
          <ComparisonBar
            label="Diamètre"
            value={info.diameter_km}
            earthValue={12_756}
            format={(n) => `${(n / 1000).toFixed(0)} ×10³ km`}
            help="Diamètre relatif à celui de la Terre (12 756 km)."
          />
          <ComparisonBar
            label="Distance au Soleil"
            value={info.distance_au}
            earthValue={1.0}
            unit="UA"
            help="1 UA = 149,6 millions de km (distance Terre-Soleil)."
          />
          <ComparisonBar
            label="Année"
            value={info.year_earth_days}
            earthValue={365.25}
            format={formatYear}
            help="Durée d'une révolution complète autour du Soleil."
          />
          <ComparisonBar
            label="Gravité"
            value={info.gravity_vs_earth}
            earthValue={1}
            unit="g"
            help="Gravité de surface relative."
          />
        </div>
      )}

      {/* Lignes techniques (position courante, source…) */}
      {extraLines.length > 0 && (
        <details className="fiche-tech">
          <summary>Détails techniques</summary>
          <dl>
            {extraLines.map(([k, val]) => (
              <div key={k} className="fiche-tech-row">
                <dt>{k}</dt>
                <dd>{val}</dd>
              </div>
            ))}
          </dl>
        </details>
      )}

      {info.type === 'hypothesis' && (
        <p className="fiche-foot">
          → Voir la <a href="/planet-nine">carte probabiliste du ciel</a> pour les zones où Planet 9 pourrait se trouver.
        </p>
      )}
    </>
  );
}

function Stat({ label, value, sub, help }: { label: string; value: string; sub?: string; help?: string }) {
  return (
    <div className="fiche-stat" title={help}>
      <div className="fiche-stat-label">{label}</div>
      <div className="fiche-stat-value">{value}</div>
      {sub && <div className="fiche-stat-sub">{sub}</div>}
    </div>
  );
}

export default function DetailPanel() {
  const sel = useSelection((s) => s.current);
  const clear = useSelection((s) => s.clear);
  const tJd = useTimeline((s) => s.tJd);
  const planetVecs = usePlanetsStore((s) => s.vectors);
  const bodies = useSmallBodies((s) => s.bodies);

  const content = useMemo(() => {
    if (!sel) return null;

    if (sel.kind === 'planet') {
      // Lookup direct par sel.id : couvre planètes (199-899), Soleil (10) et
      // Planet 9 ('planet9') qui ne sont pas dans PLANETS[]
      const info = getInfoById(sel.id);
      if (!info) {
        const planet = PLANETS.find((p) => p.id === sel.id);
        return planet ? { fallback: planet.name } : { fallback: sel.name };
      }
      const planet = PLANETS.find((p) => p.id === sel.id);
      const extra: [string, string][] = [];
      // Position live disponible uniquement pour les 8 planètes principales
      // (le Soleil est par définition à l'origine, Planet 9 est hypothétique)
      if (planet) {
        const pts = planetVecs[planet.id];
        if (pts && pts.length >= 2) {
          const [x, y, z] = positionAt(pts, tJd);
          const r = Math.hypot(x, y, z);
          extra.push(['Date courante', jdToIso(tJd)]);
          extra.push(['Position (X, Y, Z)', `(${x.toFixed(3)}, ${y.toFixed(3)}, ${z.toFixed(3)}) UA`]);
          extra.push(['Distance courante', `${r.toFixed(3)} UA`]);
          extra.push(['Source position', 'JPL Horizons VECTORS']);
          extra.push(['Frame', 'ECLIPJ2000']);
        } else {
          extra.push(['Position', 'Données Horizons en cours de chargement…']);
        }
      } else if (sel.id === '10') {
        extra.push(['Position', 'Centre du système solaire (origine)']);
      } else if (sel.id === 'planet9') {
        extra.push(['Position', '⚠ NON DÉTERMINÉE — voir carte de probabilité 2D']);
        extra.push(['Priors', 'Brown & Batygin 2021 + wide_orbit 2024–2025']);
      }
      extra.push(['Données fact-sheet', info.type === 'hypothesis'
        ? 'Brown & Batygin 2021 (estimations)'
        : 'NASA Planetary Fact Sheet']);
      return { info, extra };
    }

    if (sel.kind === 'small_body') {
      const b = bodies.find((x) => x.key === sel.id);
      if (!b) return null;
      const info = getInfoBySmallBodyKey(b.key);
      const extra: [string, string][] = [
        ['Source', b.source],
        ['a (demi-grand axe)', b.elements.a_au ? `${b.elements.a_au.toFixed(1)} UA` : '—'],
        ['e (excentricité)', b.elements.e?.toFixed(3) ?? '—'],
        ['i (inclinaison)', b.elements.i_deg ? `${b.elements.i_deg.toFixed(1)}°` : '—'],
        ['q (périhélie)', b.elements.q_au ? `${b.elements.q_au.toFixed(1)} UA` : '—'],
        ['Q (aphélie)', b.elements.Q_au ? `${b.elements.Q_au.toFixed(1)} UA` : '—'],
      ];
      if (b.position_uncertain) {
        extra.push(['⚠ Statut', 'Ω, ω, M absents — orientation orbitale non déterminée']);
      }
      if (info) return { info, extra };
      // Pas de fiche pour ce corps → mode minimal
      return { fallback: b.name, extra };
    }

    return null;
  }, [sel, planetVecs, bodies, tJd]);

  if (!sel || !content) return null;

  return (
    <div className="detail-panel">
      <button className="detail-close" onClick={clear} aria-label="Fermer">×</button>
      {('info' in content && content.info) ? (
        <DetailFiche info={content.info} extraLines={content.extra ?? []} />
      ) : (
        <div className="fiche-fallback">
          <h2>{content.fallback}</h2>
          <p style={{ color: 'var(--text-dim)' }}>
            Pas de fiche détaillée pour cet objet. Voir détails techniques ci-dessous.
          </p>
          {content.extra && (
            <dl className="fiche-tech-row-list">
              {content.extra.map(([k, v]) => (
                <div key={k} className="fiche-tech-row"><dt>{k}</dt><dd>{v}</dd></div>
              ))}
            </dl>
          )}
        </div>
      )}
    </div>
  );
}
